import { reportBoundaryError } from '@/lib/observability/faro-client';
import { isApplyingUpdate } from '@/lib/services/app-updater-service';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary for the dashboard.
 *
 * React unmounts the entire tree when a render throw reaches the root, so
 * without this the staff see a blank screen mid-shift with no way back. Must be
 * a CLASS: `componentDidCatch` / `getDerivedStateFromError` have no hooks
 * equivalent.
 *
 * Note this only catches errors thrown during RENDER, in lifecycles, and in
 * constructors of the tree below it. Errors inside event handlers and async
 * callbacks are not caught by any boundary — those still reach `window.onerror`
 * and Faro's own instrumentation, so they were never the blank-screen case.
 *
 * Recovery is a full reload rather than a state reset: Framework7 owns router
 * and DOM state outside React, so clearing `error` would drop us back into a
 * tree whose navigation state may be exactly what broke.
 */
class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // A throw while the OTA swap tears the webview down is expected teardown
    // noise, not a fault - reporting it would fill Grafana with a "crash" that
    // resolves itself one reload later.
    if (isApplyingUpdate()) return;
    // Report FIRST — see reportBoundaryError: a boundary that only renders a
    // fallback makes the failure invisible in Grafana.
    reportBoundaryError(error, info.componentStack ?? undefined);
    console.error('[AppErrorBoundary]', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    // An OTA bundle is being activated: `CapacitorUpdater.set` is about to
    // reload the app out from under us. Nothing is broken and there is nothing
    // to recover - say so, and offer no Reload (the reload is already coming).
    if (isApplyingUpdate()) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100dvh',
            color: '#8e8e93',
          }}
        >
          Updating…
        </div>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100dvh',
          gap: 16,
          padding: '0 32px',
          textAlign: 'center',
        }}
      >
        <i className="f7-icons" style={{ fontSize: 48, color: '#ff3b30' }}>
          exclamationmark_triangle
        </i>
        <p style={{ margin: 0, fontWeight: 600 }}>Something went wrong</p>
        <p style={{ margin: 0, color: '#8e8e93', maxWidth: '40ch' }}>
          The app hit an unexpected problem. Reloading usually fixes it.
        </p>
        <button
          type="button"
          className="button button-fill button-round"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    );
  }
}

export default AppErrorBoundary;
